<template>
  <Card class="layout-card complaint-order-page">
    <div class="page-scroll">
      <h2 class="page-title">订单管理</h2>

      <div class="query-card">
        <div class="query-card-header">
          <Icon type="md-mail" color="#1677ff" size="18" />
          <strong>邮件客诉查询</strong>
          <Tooltip transfer :max-width="420" placement="right-start">
            <Icon class="query-help" type="md-help-circle" size="17" />
            <div slot="content" class="query-help-content">
              <p>1. 优先填写客诉用户在App中显示的ID，也支持用户主键ID。</p>
              <p>2. 查询内部订单主键ID或TAP、TAD订单号时，不需要选择支付平台。</p>
              <p>3. 查询三方平台订单号时，请先选择对应平台，再输入用户提供的订单号。</p>
              <p>4. 平台尚未确认支付成功时，只能核对用户和候选订单，不能补单。</p>
              <p>5. Apple 内购无法进行客诉处理。</p>
            </div>
          </Tooltip>
        </div>
        <div class="query-card-body">
          <Input
            v-model.trim="searchUserId"
            size="large"
            clearable
            placeholder="App中显示的用户ID或用户主键ID（选填）"
            @on-enter="handleSearch"
          >
            <Icon slot="prefix" type="md-person" color="#1677ff" />
          </Input>
          <div class="platform-order-query">
            <Select
              v-model="searchPayPlatform"
              size="large"
              clearable
              placeholder="支付平台（选填）"
              class="channel-select"
            >
              <Option
                v-for="item in payPlatformOptions"
                :key="item.value"
                :value="item.value"
              >
                {{ item.label }}
              </Option>
            </Select>
            <Input
              v-model.trim="searchOrderNo"
              size="large"
              clearable
              :placeholder="orderReferencePlaceholder"
              @on-enter="handleSearch"
            >
              <Icon slot="prefix" type="md-document" color="#1677ff" />
            </Input>
          </div>
          <div class="goods-filter-query">
            <Select
              v-model="searchGoodsTypes"
              multiple
              clearable
              size="large"
              :max-tag-count="1"
              :disabled="Boolean(searchSubscriptionScope)"
              placeholder="商品类型（可多选）"
            >
              <Option
                v-for="item in goodsTypeOptions"
                :key="item.value"
                :value="item.value"
              >
                {{ item.label }}
              </Option>
            </Select>
            <Select
              v-model="searchSubscriptionScope"
              clearable
              size="large"
              placeholder="是否订阅订单"
              @on-change="handleSubscriptionScopeChange"
            >
              <Option value="subscription">订阅订单</Option>
              <Option value="nonSubscription">非订阅订单</Option>
            </Select>
          </div>
          <Button
            type="primary"
            size="large"
            icon="md-search"
            :loading="searchLoading"
            @click="handleSearch"
          >
            查询订单
          </Button>
          <Button size="large" icon="md-trash" @click="handleClear">
            清空
          </Button>
        </div>
      </div>

      <div v-if="hasSearched" class="result-card">
        <div class="result-card-header">
          <div>
            <strong>查询结果</strong>
            <Tag :color="searchResultTagColor">
              {{ searchResultTagText }}
            </Tag>
          </div>
        </div>
        <div v-if="searchResult.message" class="search-conclusion">
          <Alert
            :type="searchResult.associationRisk ? 'warning' : records.length ? 'success' : 'info'"
            show-icon
          >
            <strong>{{ searchResult.message }}</strong>
            <p v-if="searchResult.nextAction">{{ searchResult.nextAction }}</p>
          </Alert>
          <div v-if="supplementDecision.planName" class="supplement-plan">
            <strong>建议方案：{{ supplementDecision.planName }}</strong>
            <ol v-if="supplementDecision.planSteps && supplementDecision.planSteps.length">
              <li v-for="(step, index) in supplementDecision.planSteps" :key="`plan-${index}`">
                {{ step }}
              </li>
            </ol>
          </div>
          <Alert
            v-for="(warning, index) in searchResult.riskWarnings || []"
            :key="`risk-${index}`"
            type="warning"
            show-icon
          >
            {{ warning }}
          </Alert>
          <Alert
            v-if="showUserMatchResult"
            :type="searchResult.matchedUserPrimaryId ? 'success' : 'warning'"
            show-icon
          >
            <strong>{{ searchResult.userMatchMessage }}</strong>
            <div v-if="searchResult.matchedUserPrimaryId" class="matched-user-info">
              <span>用户主键ID：{{ searchResult.matchedUserPrimaryId }}</span>
              <span>App显示ID：{{ displayValue(searchResult.matchedUserPublicId) }}</span>
              <span>内部用户ID：{{ displayValue(searchResult.matchedUserId) }}</span>
              <span v-if="searchResult.matchedUserName">
                用户昵称：{{ searchResult.matchedUserName }}
              </span>
            </div>
          </Alert>
          <Alert
            v-if="searchResult.requiredInformation && searchResult.requiredInformation.length"
            type="info"
            show-icon
          >
            还需补充：{{ searchResult.requiredInformation.join('、') }}
          </Alert>
        </div>
        <div class="result-card-body">
          <template v-if="records.length">
            <Table
              border
              :columns="columns"
              :data="records"
              :loading="searchLoading"
            >
            <template slot="userId" slot-scope="{ row }">
              <div class="dual-user-id">
                <CopyableText :value="row.publicUserId" code />
                <small>主键 <CopyableText :value="row.userId" code /></small>
              </div>
            </template>
            <template slot="orderId" slot-scope="{ row }">
              <CopyableText :value="row.orderId" code />
            </template>
            <template slot="orderNo" slot-scope="{ row }">
              <CopyableText :value="row.orderNo" code />
            </template>
            <template slot="transactionId" slot-scope="{ row }">
              <CopyableText :value="row.transactionId" code />
            </template>
            <template slot="subscriptionId" slot-scope="{ row }">
              <CopyableText :value="row.subscriptionId" code />
            </template>
            <template slot="goodsName" slot-scope="{ row }">
              {{ displayValue(row.goodsName) }}
            </template>
            <template slot="goodsType" slot-scope="{ row }">
              {{ row.goodsTypeName || getGoodsTypeLabel(row.goodsType) }}
            </template>
            <template slot="payPlatform" slot-scope="{ row }">
              {{ row.payPlatformName || getPayPlatformLabel(row.payPlatform) }}
            </template>
            <template slot="amount" slot-scope="{ row }">
              <div class="amount-cell">
                <strong>
                  {{ formatTableAmount(row.amount, row.currency) }}
                </strong>
                <small v-if="hasRefundedAmount(row)">
                  已退 {{ formatTableAmount(row.refundedAmount, row.currency) }}
                </small>
              </div>
            </template>
            <template slot="payStatus" slot-scope="{ row }">
              <Tag :color="getPayStatusColor(row.payStatus)">
                {{ getPayStatusLabel(row.payStatus) }}
              </Tag>
            </template>
            <template slot="refundStatus" slot-scope="{ row }">
              <Tag :color="getRefundStatusColor(row.refundStatus)">
                {{ getRefundStatusLabel(row.refundStatus) }}
              </Tag>
            </template>
            <template slot="status" slot-scope="{ row }">
              <div v-if="row.goodsType === 0" class="coin-change">
                <span v-if="row.payCoinGranted">
                  +{{ row.payCoinGranted }}充值金币
                </span>
                <span v-if="row.bonusCoinGranted">
                  +{{ row.bonusCoinGranted }}赠送金币
                </span>
                <span v-if="!row.payCoinGranted && !row.bonusCoinGranted">
                  —
                </span>
              </div>
              <Tag
                v-else
                :color="getSubscriptionStatusColor(row.subscriptionStatus)"
              >
                {{
                  row.subscriptionStatusName ||
                  getSubscriptionStatusLabel(row.subscriptionStatus)
                }}
              </Tag>
            </template>
            <template slot="payTime" slot-scope="{ row }">
              {{ formatDateTime(row.payTime) }}
            </template>
            <template slot="action" slot-scope="{ row }">
              <div class="action-buttons">
                <Tooltip
                  transfer
                  :max-width="320"
                  placement="top-end"
                >
                  <div slot="content" class="supplement-tooltip-content">
                    {{ getSupplementMessage(row) }}
                  </div>
                  <span>
                    <Button
                      type="primary"
                      size="small"
                      icon="md-checkmark-circle"
                      :disabled="!row.orderId"
                      :loading="isActionLoading(row, 'supplement')"
                      @click="handleOpenOperation('supplement', row)"
                    >
                      补单
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  transfer
                  :content="getActionMessage(row.refundAction)"
                  :disabled="isActionAvailable(row.refundAction)"
                >
                  <span>
                    <Button
                      size="small"
                      icon="logo-usd"
                      :disabled="!isActionAvailable(row.refundAction)"
                      :loading="isActionLoading(row, 'refund')"
                      @click="handleOpenOperation('refund', row)"
                    >
                      退款
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  transfer
                  :content="getActionMessage(row.cancelSubscriptionAction)"
                  :disabled="isActionAvailable(row.cancelSubscriptionAction)"
                >
                  <span>
                    <Button
                      type="error"
                      ghost
                      size="small"
                      icon="md-close"
                      :disabled="
                        !isActionAvailable(row.cancelSubscriptionAction)
                      "
                      :loading="isActionLoading(row, 'cancel')"
                      @click="handleOpenOperation('cancel', row)"
                    >
                      取消订阅
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  v-if="canQuerySubscription(row)"
                  size="small"
                  icon="md-sync"
                  :loading="isActionLoading(row, 'subscription-status')"
                  @click="handleQuerySubscription(row)"
                >
                  订阅状态
                </Button>
              </div>
            </template>
            </Table>
            <Pages
              v-if="orderPage.total"
              class="list-pagination"
              :page="orderPage"
              :func="queryOrder"
            />
          </template>
          <div
            v-else-if="searchResult.platformLookup && searchResult.platformLookup.found"
            class="missing-order-result"
          >
            <div class="section-heading">
              <div>
                <strong>平台支付记录</strong>
                <span>平台有记录，本地订单尚未可靠命中</span>
              </div>
              <Button
                v-if="canCreateMissingOrder"
                type="primary"
                icon="md-add-circle"
                @click="openMissingSupplement"
              >
                {{ nearbyOrders.length ? '候选均不匹配，补建新订单' : '补建订单' }}
              </Button>
            </div>
            <div class="description-grid platform-summary">
              <div class="description-item">
                <span>平台交易号</span>
                <CopyableText :value="platformLookup.paymentId || platformLookup.reference" code />
              </div>
              <div class="description-item">
                <span>支付状态</span>
                <Tag :color="platformLookup.refunded ? 'error' : platformPaymentStatusColor">
                  {{ displayValue(platformLookup.status) }}
                </Tag>
              </div>
              <div class="description-item">
                <span>退款状态</span>
                <Tag :color="platformLookup.refunded ? 'error' : 'default'">
                  {{ platformLookup.refunded ? displayValue(platformLookup.refundStatus) : '未发现退款' }}
                </Tag>
              </div>
              <div v-if="platformLookup.refundedAmount !== null && platformLookup.refundedAmount !== undefined" class="description-item">
                <span>平台累计退款</span>
                <strong>{{ formatTableAmount(platformLookup.refundedAmount, platformLookup.currency) }}</strong>
              </div>
              <div class="description-item">
                <span>平台支付时间</span>
                <strong>{{ formatDateTime(platformLookup.payTime) }}</strong>
              </div>
              <div class="description-item">
                <span>商品标识</span>
                <CopyableText :value="platformLookup.productId" code />
              </div>
              <div class="description-item">
                <span>支付金额</span>
                <strong>{{ formatTableAmount(platformLookup.amountValue, platformLookup.currency) }}</strong>
              </div>
              <div class="description-item">
                <span>本地关联结论</span>
                <strong>{{ displayValue(platformLookup.localMatchTypeName) }}</strong>
              </div>
            </div>

            <div v-if="goodsCandidates.length" class="candidate-section">
              <div class="section-heading compact">
                <div>
                  <strong>商品候选</strong>
                </div>
              </div>
              <Table border size="small" :columns="goodsCandidateColumns" :data="goodsCandidates" />
            </div>

            <div v-if="associationCandidates.length" class="candidate-section">
              <div class="section-heading compact">
                <div>
                  <strong>疑似关联订单</strong>
                  <span>同一GPA或purchaseToken命中的订单，请重点核对支付时间</span>
                </div>
              </div>
              <Table border size="small" :columns="orderCandidateColumns" :data="associationCandidates">
                <template slot="candidateUserId" slot-scope="{ row }">
                  <div class="dual-user-id">
                    <CopyableText :value="row.publicUserId" code />
                    <small>主键 <CopyableText :value="row.userId" code /></small>
                  </div>
                </template>
                <template slot="candidatePayStatus" slot-scope="{ row }">
                  <Tag :color="getPayStatusColor(row.payStatus)">{{ getPayStatusLabel(row.payStatus) }}</Tag>
                </template>
                <template slot="candidateAction" slot-scope="{ row }">
                  <span v-if="!row.supplementDecision">仅供核对</span>
                </template>
              </Table>
            </div>

            <div v-if="nearbyOrders.length" class="candidate-section">
              <div class="section-heading compact">
                <div>
                  <strong>可能需要补单的本地订单</strong>
                  <span>同一用户、同一平台，并在平台支付前10分钟内创建；请重点核对商品和创建时间</span>
                </div>
              </div>
              <Table border size="small" :columns="orderCandidateColumns" :data="nearbyOrders">
                <template slot="candidateUserId" slot-scope="{ row }">
                  <div class="dual-user-id">
                    <CopyableText :value="row.publicUserId" code />
                    <small>主键 <CopyableText :value="row.userId" code /></small>
                  </div>
                </template>
                <template slot="candidatePayStatus" slot-scope="{ row }">
                  <Tag :color="getPayStatusColor(row.payStatus)">{{ getPayStatusLabel(row.payStatus) }}</Tag>
                </template>
                <template slot="candidateAction" slot-scope="{ row }">
                  <Tooltip transfer :max-width="320" placement="top-end">
                    <div slot="content" class="supplement-tooltip-content">
                      {{ getCandidateSupplementMessage(row) }}
                    </div>
                    <span>
                      <Button
                        type="primary"
                        size="small"
                        icon="md-checkmark-circle"
                        :disabled="!canSupplementCandidate(row)"
                        :loading="isActionLoading(row, 'supplement')"
                        @click="handleOpenOperation('supplement', row)"
                      >
                        选择并预检
                      </Button>
                    </span>
                  </Tooltip>
                </template>
              </Table>
            </div>
          </div>
          <div v-else class="empty-result">
            <Icon type="md-document" size="54" color="#d9dfe8" />
            <p>未找到符合查询条件的订单，请确认查询条件是否正确</p>
          </div>
        </div>
      </div>
    </div>

    <Modal
      v-model="actionVisible"
      :width="operationType === 'refund' ? 920 : 760"
      :mask-closable="false"
    >
      <div slot="header" class="modal-title">
        <Icon
          :type="
            operationType === 'supplement' ? 'md-checkmark-circle' : 'logo-usd'
          "
          :color="operationType === 'supplement' ? '#1677ff' : '#faad14'"
          size="18"
        />
        <span>{{ actionTitle }}</span>
      </div>
      <div v-if="actionVisible">
        <div class="mail-confirm-banner">
          <Icon type="md-mail" color="#52c41a" size="17" />
          <span>
            来自用户id：<CopyableText
              :value="selectedOrder.userId"
              strong
            />，订单号：<CopyableText
              :value="selectedOrder.orderNo"
              strong
            />，请确认
          </span>
        </div>

        <div class="description-grid order-description">
          <div class="description-item">
            <span>用户ID</span>
            <CopyableText :value="selectedOrder.userId" mono strong />
          </div>
          <div class="description-item description-item-wrap">
            <span>商品名称</span>
            <strong>{{ displayValue(selectedOrder.goodsName) }}</strong>
          </div>
          <div class="description-item">
            <span>商品类型</span>
            <Tag>
              {{
                selectedOrder.goodsTypeName ||
                getGoodsTypeLabel(selectedOrder.goodsType)
              }}
            </Tag>
          </div>
          <div class="description-item">
            <span>支付方式</span>
            <strong>{{
              selectedOrder.payPlatformName ||
              getPayPlatformLabel(selectedOrder.payPlatform)
            }}</strong>
          </div>
          <div class="description-item">
            <span>支付金额</span>
            <strong>{{
              formatTableAmount(selectedOrder.amount, selectedOrder.currency)
            }}</strong>
          </div>
          <div class="description-item">
            <span>支付状态</span>
            <Tag :color="getPayStatusColor(selectedOrder.payStatus)">
              {{ getPayStatusLabel(selectedOrder.payStatus) }}
            </Tag>
          </div>
          <div class="description-item">
            <span>平台交易ID</span>
            <CopyableText :value="selectedOrder.transactionId" code />
          </div>
          <div class="description-item description-item-wrap">
            <span>订阅ID</span>
            <CopyableText :value="selectedOrder.subscriptionId" code />
          </div>
          <div class="description-item">
            <span>可退款金额</span>
            <strong>{{
              formatTableAmount(
                selectedOrder.refundableAmount,
                selectedOrder.currency
              )
            }}</strong>
          </div>
        </div>

        <div class="user-info-card">
          <div class="user-info-card-header">用户信息</div>
          <div class="user-info-grid">
            <div class="user-info-row">
              <strong class="user-info-category">金币</strong>
              <div class="user-info-values user-info-values-three">
                <div class="user-info-metric">
                  <span>总金币</span>
                  <strong>{{ displayValue(currentEquity.balanceNum, 0) }}</strong>
                </div>
                <div class="user-info-metric">
                  <span>充值金币</span>
                  <strong>{{ displayValue(currentEquity.payBalance, 0) }}</strong>
                </div>
                <div class="user-info-metric">
                  <span>赠送金币</span>
                  <strong>{{ displayValue(currentEquity.bonusBalance, 0) }}</strong>
                </div>
              </div>
            </div>
            <div class="user-info-row">
              <strong class="user-info-category">VIP</strong>
              <div class="user-info-values">
                <div class="user-info-metric">
                  <span>当前状态</span>
                  <Tag :color="currentEquity.vipStatus ? 'success' : 'default'">
                    {{ formatBoolean(currentEquity.vipStatus) }}
                  </Tag>
                </div>
                <div class="user-info-metric">
                  <span>有效期至</span>
                  <strong>{{ formatDateTime(currentEquity.vipExpireTime) }}</strong>
                </div>
              </div>
            </div>
            <div class="user-info-row">
              <strong class="user-info-category">通行证</strong>
              <div class="user-info-values">
                <div class="user-info-metric">
                  <span>当前状态</span>
                  <Tag :color="currentEquity.passStatus ? 'success' : 'default'">
                    {{ formatBoolean(currentEquity.passStatus) }}
                  </Tag>
                </div>
                <div class="user-info-metric">
                  <span>有效期至</span>
                  <strong>{{ formatDateTime(currentEquity.passExpireTime) }}</strong>
                </div>
              </div>
            </div>
            <div class="user-info-row">
              <strong class="user-info-category">付费集</strong>
              <div class="user-info-values user-info-values-single">
                <div class="user-info-metric">
                  <span>是否解锁</span>
                  <Tag :color="hasUsedEquity ? 'warning' : 'default'">
                    {{ hasUsedEquity ? '是' : '否' }}
                  </Tag>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="operationType === 'supplement' && supplementEquityPreview.estimatedEquity"
          class="equity-estimate-card"
        >
          <div class="user-info-card-header">补单权益预估</div>
          <Alert type="info" show-icon>
            {{ supplementEquityPreview.message }}
          </Alert>
          <Table
            border
            size="small"
            :columns="supplementEquityColumns"
            :data="supplementEquityRows"
          />
        </div>

        <Alert
          v-if="operationType === 'refund' && operationForm.deductEquity && refundCheck.message"
          :type="refundCheck.forceRequired ? 'warning' : 'info'"
          show-icon
          class="modal-alert"
        >
          {{ refundCheck.message }}
        </Alert>

        <Form
          ref="operationFormRef"
          :model="operationForm"
          :rules="operationRules"
          label-position="top"
          @submit.native.prevent
        >
          <template v-if="operationType === 'refund'">
            <FormItem label="退款金额" prop="refundAmount">
              <InputNumber
                v-model="operationForm.refundAmount"
                :min="0.01"
                :max="maxRefundAmount"
                :step="0.01"
                style="width: 300px"
              />
              <span class="currency-label">
                {{ selectedOrder.currency || 'USD' }}
              </span>
            </FormItem>
            <FormItem label="补充说明（选填）">
              <Input
                v-model.trim="operationForm.remark"
                type="textarea"
                :rows="2"
                placeholder="备注信息，仅用于后台记录"
              />
            </FormItem>
            <FormItem>
              <Checkbox v-model="operationForm.deductEquity">
                退款后尝试扣回本订单权益
              </Checkbox>
              <p class="form-tip">不勾选时只退款，历史权益快照缺失或权益已使用不会阻断退款；该选择会写入售后记录。</p>
            </FormItem>
            <FormItem v-if="operationForm.deductEquity && refundCheck.forceRequired" prop="allowUsedEquity">
              <Checkbox v-model="operationForm.allowUsedEquity">
                我已知晓权益已使用或使用情况无法确认，仍允许退款
              </Checkbox>
            </FormItem>
          </template>
          <FormItem prop="confirmed">
            <Checkbox v-model="operationForm.confirmed">
              {{
                operationType === 'supplement'
                  ? '我已核实用户邮件诉求，确认信息无误，同意补发权益'
                  : '我已核实用户邮件诉求，确认信息无误，同意执行退款'
              }}
            </Checkbox>
          </FormItem>
        </Form>
      </div>
      <div slot="footer">
        <Button @click="actionVisible = false">取消</Button>
        <Button
          :type="operationType === 'refund' ? 'error' : 'primary'"
          :loading="submitLoading"
          @click="handleActionConfirm"
        >
          确认{{ actionTitle }}
        </Button>
      </div>
    </Modal>

    <Modal v-model="missingSupplementVisible" :width="720" :mask-closable="false">
      <div slot="header" class="modal-title">
        <Icon type="md-add-circle" color="#1677ff" size="18" />
        <span>{{ supplementDecision.planName || '补建平台已支付订单' }}</span>
      </div>
      <div class="missing-supplement-form">
        <Alert :type="searchResult.associationRisk ? 'warning' : 'info'" show-icon>
          {{ supplementDecision.message || '提交时后端会再次查询支付平台并检查本地关联，避免重复补单' }}
        </Alert>
        <div v-if="supplementDecision.planSteps && supplementDecision.planSteps.length" class="supplement-plan">
          <strong>处理步骤</strong>
          <ol>
            <li v-for="(step, index) in supplementDecision.planSteps" :key="`modal-plan-${index}`">
              {{ step }}
            </li>
          </ol>
        </div>
        <div class="description-grid platform-summary">
          <div class="description-item">
            <span>支付平台</span>
            <strong>{{ searchResult.payPlatformName }}</strong>
          </div>
          <div class="description-item">
            <span>平台交易号</span>
            <CopyableText :value="platformLookup.reference" code />
          </div>
          <div class="description-item">
            <span>支付时间</span>
            <strong>{{ formatDateTime(platformLookup.payTime) }}</strong>
          </div>
          <div class="description-item">
            <span>平台商品</span>
            <CopyableText :value="platformLookup.productId" code />
          </div>
        </div>
        <Form label-position="top" @submit.native.prevent>
          <FormItem v-if="isCompleteMissingOrder" label="用户ID" required>
            <Input
              v-model.trim="missingSupplementForm.userReference"
              placeholder="支持用户主键ID、user_id或public_user_id"
              @on-blur="loadMissingSupplementEquityPreview"
            />
          </FormItem>
          <FormItem v-if="isCompleteMissingOrder" label="商品" required>
            <Select
              v-if="goodsCandidates.length"
              v-model="missingSupplementForm.goodsId"
              filterable
              @on-change="loadMissingSupplementEquityPreview"
            >
              <Option
                v-for="item in goodsCandidates"
                :key="item.goodsId"
                :value="item.goodsId"
              >
                {{ item.goodsName }}（ID {{ item.goodsId }}，{{ item.matchedDiscountKey ? '折扣商品' : '普通商品' }}）
              </Option>
            </Select>
            <InputNumber
              v-else
              v-model="missingSupplementForm.goodsId"
              :min="1"
              :precision="0"
              style="width: 100%"
              placeholder="平台未返回可匹配商品，请人工填写商品主键ID"
              @on-blur="loadMissingSupplementEquityPreview"
            />
          </FormItem>
          <div v-if="missingSupplementEquityLoading" class="estimate-loading">
            <Spin />
            <span>正在计算权益预估...</span>
          </div>
          <div
            v-else-if="missingSupplementEquityPreview.estimatedEquity"
            class="equity-estimate-card"
          >
            <div class="user-info-card-header">补单权益预估</div>
            <Alert type="info" show-icon>
              {{ missingSupplementEquityPreview.message }}
            </Alert>
            <Table
              border
              size="small"
              :columns="supplementEquityColumns"
              :data="missingSupplementEquityRows"
            />
          </div>
          <FormItem>
            <Checkbox v-model="missingSupplementForm.confirmed">
              我已确认平台支付成功、用户及商品信息无误，同意补建订单并发放权益
            </Checkbox>
          </FormItem>
        </Form>
      </div>
      <div slot="footer">
        <Button @click="missingSupplementVisible = false">取消</Button>
        <Button type="primary" :loading="submitLoading" @click="submitMissingSupplement">
          确认补建
        </Button>
      </div>
    </Modal>

    <Modal v-model="refundConfirmVisible" :width="520" :mask-closable="false">
      <div slot="header" class="modal-title">
        <Icon type="md-alert" color="#ff4d4f" size="18" />
        <span>二次确认退款</span>
      </div>
      <div class="refund-confirm">
        <Icon type="md-alert" size="40" color="#ff4d4f" />
        <h3>请再次确认退款信息</h3>
        <p>退款后金额将原路返回，此操作不可撤销</p>
      </div>
      <div class="description-list">
        <div>
          <span>用户ID</span>
          <CopyableText :value="selectedOrder.userId" strong />
        </div>
        <div>
          <span>订单号</span>
          <CopyableText :value="selectedOrder.orderNo" strong />
        </div>
        <div>
          <span>商品</span
          ><strong>{{ displayValue(selectedOrder.goodsName) }}</strong>
        </div>
        <div>
          <span>原支付金额</span>
          <strong>{{
            formatTableAmount(selectedOrder.amount, selectedOrder.currency)
          }}</strong>
        </div>
        <div>
          <span>本次退款金额</span>
          <strong class="refund-amount">{{
            formatTableAmount(
              operationForm.refundAmount,
              selectedOrder.currency
            )
          }}</strong>
        </div>
      </div>
      <Checkbox v-model="secondaryConfirmed">
        我已确认退款金额无误，了解退款后不可撤销
      </Checkbox>
      <div slot="footer">
        <Button @click="refundConfirmVisible = false">取消退款</Button>
        <Button type="error" :loading="submitLoading" @click="submitRefund">
          确认退款
        </Button>
      </div>
    </Modal>

    <Modal v-model="cancelVisible" :width="520" :mask-closable="false">
      <div slot="header" class="modal-title">
        <Icon type="md-alert" color="#faad14" size="18" />
        <span>取消订阅</span>
      </div>
      <div v-if="cancelVisible">
        <div class="description-list cancel-description">
          <div>
            <span>用户ID</span>
            <CopyableText :value="selectedOrder.userId" code />
          </div>
          <div>
            <span>订单号</span>
            <CopyableText :value="selectedOrder.orderNo" code />
          </div>
          <div>
            <span>商品</span
            ><strong>{{ displayValue(selectedOrder.goodsName) }}</strong>
          </div>
          <div>
            <span>支付方式</span>
            <strong>{{
              selectedOrder.payPlatformName ||
              getPayPlatformLabel(selectedOrder.payPlatform)
            }}</strong>
          </div>
          <div>
            <span>支付金额</span>
            <strong>{{
              formatTableAmount(selectedOrder.amount, selectedOrder.currency)
            }}</strong>
          </div>
        </div>
        <div class="cancel-notice">
          <strong>
            <Icon type="md-alert" />
            取消订阅须知：
          </strong>
          <p>• 订阅将立即取消，不再产生后续自动续费</p>
          <p>• 取消结果将同步记录到售后管理</p>
          <p>• 此操作不可撤销，处理后将流转到售后管理</p>
        </div>
        <Checkbox v-model="cancelConfirmed" class="cancel-checkbox">
          我已确认用户要求取消订阅，了解操作后果
        </Checkbox>
      </div>
      <div slot="footer">
        <Button @click="cancelVisible = false">取消</Button>
        <Button type="error" :loading="submitLoading" @click="submitCancel">
          确认取消订阅
        </Button>
      </div>
    </Modal>

    <Modal
      v-model="subscriptionStatusVisible"
      title="三方订阅实时状态"
      :width="680"
      :footer-hide="true"
    >
      <Spin v-if="subscriptionStatusLoading" fix />
      <div class="subscription-status-result">
        <Alert :type="subscriptionStatusAlertType" show-icon>
          {{ subscriptionStatusResult.message || '暂无查询结论' }}
        </Alert>
        <div class="description-grid">
          <div class="description-item">
            <span>支付平台</span>
            <strong>{{ displayValue(subscriptionStatusResult.payPlatformName) }}</strong>
          </div>
          <div class="description-item">
            <span>订阅ID</span>
            <CopyableText :value="subscriptionStatusResult.subscriptionId" code />
          </div>
          <div class="description-item">
            <span>本地状态</span>
            <Tag :color="getSubscriptionStatusColor(subscriptionStatusResult.localStatus)">
              {{ displayValue(subscriptionStatusResult.localStatusName) }}
            </Tag>
          </div>
          <div class="description-item">
            <span>平台实时状态</span>
            <Tag :color="platformSubscriptionStatusColor">
              {{ displayValue(subscriptionStatusResult.platformStatusName) }}
            </Tag>
          </div>
          <div class="description-item">
            <span>自动续订</span>
            <strong>{{ formatBoolean(subscriptionStatusResult.autoRenew) }}</strong>
          </div>
          <div class="description-item">
            <span>状态更新时间</span>
            <strong>{{ formatDateTime(subscriptionStatusResult.statusUpdateTime) }}</strong>
          </div>
          <div class="description-item">
            <span>订阅开启时间</span>
            <strong>{{ formatDateTime(subscriptionStatusResult.subscriptionStartTime) }}</strong>
          </div>
          <div class="description-item">
            <span>平台取消时间</span>
            <strong>{{ formatDateTime(subscriptionStatusResult.cancellationTime) }}</strong>
          </div>
          <div class="description-item">
            <span>当前周期开始</span>
            <strong>{{ formatDateTime(subscriptionStatusResult.currentPeriodStart) }}</strong>
          </div>
          <div class="description-item">
            <span>当前周期结束</span>
            <strong>{{ formatDateTime(subscriptionStatusResult.currentPeriodEnd) }}</strong>
          </div>
        </div>
      </div>
    </Modal>
  </Card>
</template>

<script>
import {
  cancelCustomerServiceSubscription,
  getCustomerServiceOrderPreview,
  getCustomerServiceSubscriptionStatus,
  previewCustomerServiceSupplementEquity,
  refundCustomerServiceOrder,
  searchCustomerServiceOrders,
  supplementCustomerServiceOrder
} from '@/api/customer-service'
import CopyableText from '../components/copyable-text.vue'
import Pages from '@/components/pages'
import {
  GOODS_TYPE_OPTIONS,
  PAY_PLATFORM_OPTIONS,
  compactObject,
  displayValue,
  formatDateTime,
  getGoodsTypeLabel,
  getPayPlatformLabel,
  getPayStatusColor,
  getPayStatusLabel,
  getRefundStatusColor,
  getRefundStatusLabel,
  getSubscriptionStatusColor,
  getSubscriptionStatusLabel
} from '../constants'

const validateConfirmed = (rule, value, callback) => {
  if (value) callback()
  else callback(new Error('请勾选确认'))
}

export default {
  name: 'ComplaintCenterOrder',
  components: {
    CopyableText,
    Pages
  },
  data() {
    return {
      payPlatformOptions: PAY_PLATFORM_OPTIONS,
      goodsTypeOptions: GOODS_TYPE_OPTIONS,
      searchPayPlatform: null,
      searchUserId: '',
      searchOrderNo: '',
      searchGoodsTypes: [],
      searchSubscriptionScope: null,
      searchLoading: false,
      hasSearched: false,
      searchResult: {},
      records: [],
      orderPage: {
        current: 1,
        size: 10,
        total: 0
      },
      actionLoading: {
        orderId: null,
        type: ''
      },
      selectedOrder: {},
      operationType: '',
      supplementAction: {},
      missingSupplementVisible: false,
      missingSupplementForm: {
        userReference: '',
        goodsId: null,
        confirmed: false
      },
      missingSupplementEquityLoading: false,
      missingSupplementEquityPreview: {},
      missingSupplementEquityRequestId: 0,
      actionVisible: false,
      operationForm: {
        refundAmount: null,
        remark: '',
        deductEquity: true,
        allowUsedEquity: false,
        confirmed: false
      },
      operationRules: {
        refundAmount: [
          {
            required: true,
            type: 'number',
            message: '请输入退款金额',
            trigger: 'change'
          }
        ],
        allowUsedEquity: [{ validator: validateConfirmed, trigger: 'change' }],
        confirmed: [{ validator: validateConfirmed, trigger: 'change' }]
      },
      refundConfirmVisible: false,
      secondaryConfirmed: false,
      cancelVisible: false,
      cancelConfirmed: false,
      subscriptionStatusVisible: false,
      subscriptionStatusLoading: false,
      subscriptionStatusResult: {},
      submitLoading: false,
      columns: [
        { title: '用户ID', slot: 'userId', width: 165, resizable: true },
        { title: '订单ID', slot: 'orderId', width: 110, resizable: true },
        { title: '订单号', slot: 'orderNo', width: 130, resizable: true },
        {
          title: '三方平台订单号',
          slot: 'transactionId',
          width: 150,
          resizable: true
        },
        {
          title: '订阅ID',
          slot: 'subscriptionId',
          width: 160,
          resizable: true
        },
        { title: '商品', slot: 'goodsName', width: 135, resizable: true },
        { title: '商品类型', slot: 'goodsType', width: 90, resizable: true },
        { title: '支付方式', slot: 'payPlatform', width: 90, resizable: true },
        { title: '金额', slot: 'amount', width: 120, resizable: true },
        { title: '支付状态', slot: 'payStatus', width: 100, resizable: true },
        {
          title: '退款状态',
          slot: 'refundStatus',
          width: 110,
          resizable: true
        },
        { title: '订阅状态', slot: 'status', width: 130, resizable: true },
        { title: '支付时间', slot: 'payTime', width: 150, resizable: true },
        {
          title: '操作',
          slot: 'action',
          fixed: 'right',
          width: 350,
          resizable: true
        }
      ],
      goodsCandidateColumns: [
        { title: '商品ID', key: 'goodsId', width: 90 },
        { title: '商品名称', key: 'goodsName', minWidth: 150 },
        { title: '商品类型', key: 'goodsTypeName', width: 100 },
        { title: '匹配key', key: 'matchedGoodsKey', minWidth: 180 },
        {
          title: '匹配方式',
          width: 110,
          render: (h, { row }) => h('span', row.matchedDiscountKey ? '折扣key' : '普通key')
        },
        { title: '配置版本', key: 'goodsVersion', width: 90 },
        {
          title: '配置价格',
          width: 110,
          render: (h, { row }) => h('span', this.formatTableAmount(row.goodsPrice, '$'))
        }
      ],
      orderCandidateColumns: [
        { title: '订单ID', key: 'orderId', width: 100 },
        { title: '用户ID', slot: 'candidateUserId', width: 165 },
        { title: '商品', key: 'goodsName', minWidth: 140 },
        {
          title: '创建时间',
          width: 160,
          render: (h, { row }) => h('span', this.formatDateTime(row.createTime))
        },
        { title: '平台订单号', key: 'transactionId', minWidth: 170 },
        {
          title: '支付时间',
          width: 160,
          render: (h, { row }) => h('span', this.formatDateTime(row.payTime))
        },
        { title: '本地支付状态', slot: 'candidatePayStatus', width: 120 },
        { title: '操作', slot: 'candidateAction', width: 140, fixed: 'right' }
      ],
      supplementEquityColumns: [
        { title: '权益', key: 'label', width: 150 },
        { title: '当前状态', key: 'current', minWidth: 180 },
        { title: '预计补单后', key: 'estimated', minWidth: 180 }
      ]
    }
  },
  computed: {
    actionTitle() {
      return this.operationType === 'supplement' ? '补单' : '退款'
    },
    currentEquity() {
      return this.selectedOrder.currentEquity || {}
    },
    supplementEquityPreview() {
      return this.selectedOrder.supplementEquityPreview || {}
    },
    supplementEquityRows() {
      return this.buildEquityEstimateRows(this.supplementEquityPreview)
    },
    missingSupplementEquityRows() {
      return this.buildEquityEstimateRows(
        this.missingSupplementEquityPreview
      )
    },
    refundCheck() {
      return this.selectedOrder.refundEquityCheck || {}
    },
    hasUsedEquity() {
      return !!(
        this.refundCheck.forceRequired ||
        (this.refundCheck.vipWatchRecords &&
          this.refundCheck.vipWatchRecords.length) ||
        this.refundCheck.payCoinUsed ||
        this.refundCheck.bonusCoinUsed
      )
    },
    maxRefundAmount() {
      if (
        this.selectedOrder.refundableAmount !== null &&
        this.selectedOrder.refundableAmount !== undefined
      ) {
        return Number(this.selectedOrder.refundableAmount) || 0
      }
      return Number(this.selectedOrder.amount) || 0
    },
    subscriptionStatusAlertType() {
      if (!this.subscriptionStatusResult.querySuccess) return 'error'
      if (this.subscriptionStatusResult.subscriptionExists === false) {
        return 'warning'
      }
      return this.subscriptionStatusResult.statusConsistent
        ? 'success'
        : 'warning'
    },
    platformSubscriptionStatusColor() {
      if (!this.subscriptionStatusResult.querySuccess) return 'error'
      if (this.subscriptionStatusResult.subscriptionExists === false) {
        return 'default'
      }
      return this.subscriptionStatusResult.statusConsistent
        ? 'success'
        : 'warning'
    },
    platformLookup() {
      return this.searchResult.platformLookup || {}
    },
    supplementDecision() {
      return this.searchResult.supplementDecision || {}
    },
    goodsCandidates() {
      return this.platformLookup.goodsCandidates || []
    },
    associationCandidates() {
      return this.searchResult.associationCandidates || []
    },
    nearbyOrders() {
      return this.searchResult.nearbyOrders || []
    },
    canCreateMissingOrder() {
      return !!(
        this.supplementDecision.available &&
        (this.searchResult.missingOrderSupplementAvailable ||
          this.searchResult.renewalSupplementAvailable)
      )
    },
    platformPaymentStatusColor() {
      if (this.searchResult.platformPaymentSuccessful === true) return 'success'
      if (this.searchResult.platformPaymentSuccessful === false) return 'warning'
      return 'default'
    },
    isCompleteMissingOrder() {
      return Number(this.searchResult.suggestedSupplementScene) === 4
    },
    orderReferencePlaceholder() {
      const examples = {
        1: '例如 GPA.3345-3251-9019-92442',
        2: '例如 430003168451381',
        3: '例如 202607281940108013001882C0250938313',
        4: '例如 pi_3Txxxxxxxx',
        5: '例如 PayPal Order ID 或 Capture ID'
      }
      return (
        examples[this.searchPayPlatform] ||
        '内部订单主键ID、TAP或TAD订单号'
      )
    },
    showUserMatchResult() {
      return !!this.searchResult.userMatchMessage
    },
    searchResultTagText() {
      if (this.records.length) return '本地已找到'
      if (this.platformLookup.found) return '平台已找到'
      return '未找到'
    },
    searchResultTagColor() {
      if (this.records.length) return 'success'
      if (this.platformLookup.found) return 'warning'
      return 'default'
    }
  },
  methods: {
    handleSearch() {
      this.queryOrder()
    },
    // “订阅订单”只是VIP和通行证的快捷筛选，后端始终只接收goodsTypes。
    handleSubscriptionScopeChange(value) {
      if (value === 'subscription') {
        this.searchGoodsTypes = [1, 2]
      } else if (value === 'nonSubscription') {
        this.searchGoodsTypes = [0]
      } else {
        this.searchGoodsTypes = []
      }
    },
    queryOrder(page) {
      if (page) this.orderPage.current = page
      else this.orderPage.current = 1
      this.searchLoading = true
      searchCustomerServiceOrders(
        compactObject({
          payPlatform: this.searchPayPlatform,
          userId: this.searchUserId,
          orderReference: this.searchOrderNo,
          goodsTypes: this.searchGoodsTypes.length ? this.searchGoodsTypes : null,
          current: this.orderPage.current,
          size: this.orderPage.size
        })
      )
        .then(({ data }) => {
          this.applySearchResult(data)
        })
        .finally(() => {
          this.searchLoading = false
        })
    },
    applySearchResult(data) {
      this.searchResult = data || {}
      this.records = (data && data.records) || []
      this.orderPage.current = Number(data && data.current) || 1
      this.orderPage.size = Number(data && data.size) || this.orderPage.size
      this.orderPage.total = Number(data && data.total) || 0
      this.hasSearched = true
    },
    handleClear() {
      this.searchPayPlatform = null
      this.searchUserId = ''
      this.searchOrderNo = ''
      this.searchGoodsTypes = []
      this.searchSubscriptionScope = null
      this.searchResult = {}
      this.records = []
      this.orderPage = {
        current: 1,
        size: 10,
        total: 0
      }
      this.hasSearched = false
    },
    openMissingSupplement() {
      const relatedOrder = this.searchResult.relatedOrder || {}
      this.missingSupplementForm = {
        userReference:
          this.searchUserId ||
          this.searchResult.matchedUserPrimaryId ||
          relatedOrder.userId ||
          '',
        goodsId:
          this.platformLookup.recommendedGoodsId || relatedOrder.goodsId || null,
        confirmed: false
      }
      this.missingSupplementEquityPreview = {}
      this.missingSupplementVisible = true
      this.$nextTick(() => this.loadMissingSupplementEquityPreview())
    },
    loadMissingSupplementEquityPreview() {
      if (!this.missingSupplementVisible) return
      const requestId = ++this.missingSupplementEquityRequestId
      if (
        this.isCompleteMissingOrder &&
        (!this.missingSupplementForm.userReference ||
          !this.missingSupplementForm.goodsId)
      ) {
        this.missingSupplementEquityPreview = {}
        this.missingSupplementEquityLoading = false
        return
      }
      this.missingSupplementEquityPreview = {}
      this.missingSupplementEquityLoading = true
      previewCustomerServiceSupplementEquity(
        compactObject({
          supplementScene: this.searchResult.suggestedSupplementScene,
          orderId: this.searchResult.relatedOrderId,
          payPlatform: this.searchResult.payPlatform,
          userReference: this.missingSupplementForm.userReference,
          goodsId: this.missingSupplementForm.goodsId,
          subscriptionId: this.searchResult.relatedSubscriptionId,
          transactionId:
            this.platformLookup.paymentId || this.platformLookup.reference,
          payTime: this.platformLookup.payTime
        })
      )
        .then(({ data }) => {
          if (requestId === this.missingSupplementEquityRequestId) {
            this.missingSupplementEquityPreview = data || {}
          }
        })
        .finally(() => {
          if (requestId === this.missingSupplementEquityRequestId) {
            this.missingSupplementEquityLoading = false
          }
        })
    },
    submitMissingSupplement() {
      if (this.isCompleteMissingOrder) {
        if (!this.missingSupplementForm.userReference) {
          this.$Message.warning('请填写需要发放权益的用户ID')
          return
        }
        if (!this.missingSupplementForm.goodsId) {
          this.$Message.warning('请选择与平台商品标识一致的商品')
          return
        }
      }
      if (!this.missingSupplementForm.confirmed) {
        this.$Message.warning('请确认补建订单和发放权益')
        return
      }
      this.submitLoading = true
      supplementCustomerServiceOrder(
        compactObject({
          supplementScene: this.searchResult.suggestedSupplementScene,
          orderId: this.searchResult.relatedOrderId,
          payPlatform: this.searchResult.payPlatform,
          userReference: this.missingSupplementForm.userReference,
          goodsId: this.missingSupplementForm.goodsId,
          // 必须保留运营原始输入，后端会重新查询平台并获得规范交易号。
          transactionId:
            this.platformLookup.paymentId || this.platformLookup.reference,
          subscriptionId: this.searchResult.relatedSubscriptionId,
          payTime: this.platformLookup.payTime,
          confirmed: true
        })
      )
        .then(({ data }) => {
          this.$Message.success((data && data.message) || '补建订单成功')
          this.missingSupplementVisible = false
          this.refreshSearch()
        })
        .finally(() => {
          this.submitLoading = false
        })
    },
    canQuerySubscription(row) {
      return !!(
        row &&
        row.orderId &&
        (row.goodsType === 1 || row.goodsType === 2) &&
        (row.payPlatform === 3 ||
          row.payPlatform === 4 ||
          row.payPlatform === 5)
      )
    },
    async handleQuerySubscription(row) {
      this.actionLoading = {
        orderId: row.orderId,
        type: 'subscription-status'
      }
      this.subscriptionStatusVisible = true
      this.subscriptionStatusLoading = true
      this.subscriptionStatusResult = {
        orderId: row.orderId,
        payPlatformName: row.payPlatformName,
        subscriptionId: row.subscriptionId
      }
      try {
        const { data } = await getCustomerServiceSubscriptionStatus(row.orderId)
        this.subscriptionStatusResult = data || {}
      } finally {
        this.subscriptionStatusLoading = false
        this.actionLoading = {
          orderId: null,
          type: ''
        }
      }
    },
    formatBoolean(value) {
      if (value === true) return '是'
      if (value === false) return '否'
      return '-'
    },
    buildEquityEstimateRows(preview) {
      const data = preview || {}
      const current = data.currentEquity || {}
      const estimated = data.estimatedEquity || {}
      const rows = []
      if (data.platformPeriodStartTime || data.platformPeriodEndTime) {
        rows.push({
          label: '订单记录的平台周期',
          current: this.formatDateTime(data.platformPeriodStartTime),
          estimated: this.formatDateTime(data.platformPeriodEndTime)
        })
      }
      if (data.deliveryEffectiveStartTime || data.deliveryEffectiveEndTime) {
        rows.push({
          label: '本次补发实际有效期',
          current: this.formatDateTime(data.deliveryEffectiveStartTime),
          estimated: this.formatDateTime(data.deliveryEffectiveEndTime)
        })
      }
      if (data.goodsType === 1) {
        rows.push({
          label: 'VIP有效期',
          current: this.formatDateTime(current.vipExpireTime),
          estimated: this.formatDateTime(estimated.vipExpireTime)
        })
      }
      if (data.goodsType === 2) {
        rows.push({
          label: '通行证有效期',
          current: this.formatDateTime(current.passExpireTime),
          estimated: this.formatDateTime(estimated.passExpireTime)
        })
      }
      if (data.goodsType === 0 || data.goodsType === 2) {
        rows.push(
          {
            label: `总金币（预计 +${Number(data.payCoinIncrement || 0) + Number(data.bonusCoinIncrement || 0)}）`,
            current: this.displayValue(current.balanceNum, 0),
            estimated: this.displayValue(estimated.balanceNum, 0)
          },
          {
            label: `充值金币（预计 +${data.payCoinIncrement || 0}）`,
            current: this.displayValue(current.payBalance, 0),
            estimated: this.displayValue(estimated.payBalance, 0)
          },
          {
            label: `赠币（预计 +${data.bonusCoinIncrement || 0}）`,
            current: this.displayValue(current.bonusBalance, 0),
            estimated: this.displayValue(estimated.bonusBalance, 0)
          }
        )
      }
      return rows
    },
    async handleOpenOperation(type, row) {
      this.actionLoading = {
        orderId: row.orderId,
        type
      }
      try {
        const { data } = await getCustomerServiceOrderPreview(row.orderId, {
          includeVipUsage: type === 'refund',
          // 平台找回历史订单时，本地记录可能缺少交易号，预检需要延续搜索时的平台核验依据。
          platformReference:
            type === 'supplement'
              ? this.resolveSupplementPlatformReference(row)
              : undefined,
          // 时间窗候选不是通过交易号直接关联，预检必须再次校验客诉用户。
          userReference:
            type === 'supplement' ? this.resolveSupplementUserReference(row) : undefined
        })
        this.selectedOrder = data || {}
        if (type === 'cancel') {
          if (
            !this.isActionAvailable(this.selectedOrder.cancelSubscriptionAction)
          ) {
            this.$Message.warning(
              this.getActionMessage(this.selectedOrder.cancelSubscriptionAction)
            )
            return
          }
          this.cancelConfirmed = false
          this.cancelVisible = true
          return
        }
        if (type === 'supplement') {
          // 补单必须以最新预检结果为准，列表数据只负责定位订单。
          this.supplementAction = this.selectedOrder.supplementDecision || {}
          if (this.supplementAction.required === false) {
            this.$Message.info(
              this.supplementAction.message || '该订单当前不需要补单'
            )
            return
          }
          if (!this.supplementAction.available) {
            this.$Message.warning(
              this.supplementAction.message || '预检未通过，暂时不能补单'
            )
            return
          }
        }
        if (
          type === 'refund' &&
          !this.isActionAvailable(this.selectedOrder.refundAction)
        ) {
          this.$Message.warning(
            this.getActionMessage(this.selectedOrder.refundAction)
          )
          return
        }
        this.operationType = type
        this.operationForm = {
          refundAmount: type === 'refund' ? this.maxRefundAmount || null : null,
          remark: '',
          deductEquity: true,
          allowUsedEquity: false,
          confirmed: false
        }
        this.actionVisible = true
      } finally {
        this.actionLoading = {
          orderId: null,
          type: ''
        }
      }
    },
    handleActionConfirm() {
      this.$refs.operationFormRef.validate((valid) => {
        if (!valid) return
        if (this.operationType === 'refund') {
          if (
            !this.operationForm.refundAmount ||
            this.operationForm.refundAmount > this.maxRefundAmount
          ) {
            this.$Message.warning('退款金额不能超过剩余可退款金额')
            return
          }
          this.secondaryConfirmed = false
          this.refundConfirmVisible = true
          return
        }
        this.submitSupplement()
      })
    },
    submitSupplement() {
      this.submitLoading = true
      supplementCustomerServiceOrder(
        compactObject({
          orderId: this.selectedOrder.orderId,
          confirmed: true,
          supplementScene: this.supplementAction.supplementScene,
          transactionId: this.resolveSupplementPlatformReference(
            this.selectedOrder
          ),
          userReference: this.resolveSupplementUserReference(
            this.selectedOrder
          ),
          subscriptionId: this.selectedOrder.subscriptionId
        })
      )
        .then(({ data }) => {
          this.$Message.success((data && data.message) || '补单成功')
          this.actionVisible = false
          this.refreshSearch()
        })
        .finally(() => {
          this.submitLoading = false
        })
    },
    submitRefund() {
      if (!this.secondaryConfirmed) {
        this.$Message.warning('请完成退款二次确认')
        return
      }
      this.submitLoading = true
      refundCustomerServiceOrder(
        compactObject({
          orderId: this.selectedOrder.orderId,
          refundAmount: this.operationForm.refundAmount,
          // currency是订单管理展示符号，平台退款接口仍必须使用标准ISO币种代码。
          refundCurrency: 'USD',
          remark: this.operationForm.remark,
          deductEquity: this.operationForm.deductEquity,
          allowUsedEquity: this.operationForm.deductEquity
            ? this.operationForm.allowUsedEquity
            : false,
          confirmed: true,
          secondaryConfirmed: true
        })
      )
        .then(({ data }) => {
          this.$Message.success((data && data.message) || '退款成功')
          this.refundConfirmVisible = false
          this.actionVisible = false
          this.refreshSearch()
        })
        .finally(() => {
          this.submitLoading = false
        })
    },
    submitCancel() {
      if (!this.cancelConfirmed) {
        this.$Message.warning('请先勾选确认')
        return
      }
      this.submitLoading = true
      cancelCustomerServiceSubscription({
        orderId: this.selectedOrder.orderId,
        cancelMode: 2,
        confirmed: true
      })
        .then(({ data }) => {
          this.$Message.success((data && data.message) || '订阅已取消')
          this.cancelVisible = false
          this.refreshSearch()
        })
        .finally(() => {
          this.submitLoading = false
        })
    },
    refreshSearch() {
      this.queryOrder()
    },
    getSupplementMessage(row) {
      const decision = row.supplementDecision || {}
      return decision.message || '点击后将先预检订单，再判断是否需要补单'
    },
    canSupplementCandidate(row) {
      return !!(
        row &&
        row.orderId &&
        row.supplementDecision &&
        row.supplementDecision.available
      )
    },
    getCandidateSupplementMessage(row) {
      const decision = (row && row.supplementDecision) || {}
      return decision.message || '该记录仅用于核对，暂时不能选择补单'
    },
    resolveSupplementPlatformReference(row) {
      const order = row || {}
      return (
        this.platformLookup.paymentId ||
        this.platformLookup.reference ||
        order.transactionId ||
        ''
      )
    },
    resolveSupplementUserReference(row) {
      return (
        this.searchUserId ||
        this.searchResult.matchedUserPrimaryId ||
        (row && row.userId) ||
        ''
      )
    },
    hasRefundedAmount(row) {
      return Number(row.refundedAmount) > 0
    },
    isActionAvailable(action) {
      return !!(action && action.available)
    },
    getActionMessage(action) {
      return action && action.message
        ? action.message
        : '当前订单不可执行该操作'
    },
    isActionLoading(row, type) {
      return (
        this.actionLoading.orderId === row.orderId &&
        this.actionLoading.type === type
      )
    },
    formatTableAmount(amount, currency) {
      if (amount === null || amount === undefined || amount === '') return '-'
      const value = Number(amount)
      if (Number.isNaN(value)) return '-'
      const code = currency === 'USD' ? '$' : currency || '$'
      return `${code} ${value.toFixed(2)}`
    },
    displayValue,
    formatDateTime,
    getGoodsTypeLabel,
    getPayPlatformLabel,
    getPayStatusColor,
    getPayStatusLabel,
    getRefundStatusColor,
    getRefundStatusLabel,
    getSubscriptionStatusColor,
    getSubscriptionStatusLabel
  }
}
</script>

<style scoped lang="less">
.layout-card {
  height: 100%;

  /deep/ .ivu-card-body {
    height: 100%;
    padding: 0;
  }
}

.page-scroll {
  height: 100%;
  padding: 24px;
  overflow: auto;
}

.page-title {
  margin-bottom: 16px;
  color: #141414;
  font-size: 20px;
  line-height: 28px;
}

.query-card,
.result-card {
  overflow: hidden;
  margin-bottom: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fff;
}

.query-card {
  border-left: 3px solid #1677ff;
}

.query-card-header,
.result-card-header {
  display: flex;
  align-items: center;
  height: 46px;
  padding: 0 16px;
  border-bottom: 1px solid #f0f0f0;
}

.query-card-header {
  gap: 8px;
}

.query-help {
  color: #808695;
  cursor: help;
}

.query-help-content,
.supplement-tooltip-content {
  line-height: 20px;
  white-space: normal;
  overflow-wrap: anywhere;
}

.query-help-content p + p {
  margin-top: 6px;
}

.query-card-body {
  display: grid;
  grid-template-columns: minmax(230px, 0.7fr) minmax(460px, 1.35fr) minmax(330px, 1fr) auto auto;
  gap: 16px;
  padding: 16px;
}

.platform-order-query {
  display: grid;
  grid-template-columns: 180px minmax(320px, 1fr);
  gap: 8px;
  min-width: 0;
}

.goods-filter-query {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) 150px;
  gap: 8px;
  min-width: 0;
}

.result-card-header > div {
  display: flex;
  align-items: center;
  gap: 8px;
}

.result-card-body {
  padding: 16px;

  /deep/ .ivu-table-header,
  /deep/ .ivu-table-fixed-header {
    height: 54px;
  }

  /deep/ .ivu-table-header th,
  /deep/ .ivu-table-fixed-header th {
    height: 54px;
    background: #fafafa;
    color: #141414;
    font-weight: 600;
  }

  /deep/ .ivu-table-cell {
    white-space: nowrap;
    word-break: normal;
  }

  /deep/ .ivu-table-body td,
  /deep/ .ivu-table-fixed-body td {
    height: 60px;
  }

  /deep/ .ivu-table-body tr,
  /deep/ .ivu-table-fixed-body tr {
    height: 60px;
  }
}

.search-conclusion {
  padding: 16px 16px 0;

  /deep/ .ivu-alert + .ivu-alert {
    margin-top: 8px;
  }

  p {
    margin-top: 4px;
  }
}

.supplement-plan {
  margin: 8px 0 12px;
  padding: 10px 14px;
  color: #355070;
  background: #f5f9ff;
  border: 1px solid #d6e8ff;
  border-radius: 4px;

  ol {
    margin: 6px 0 0 20px;
    padding: 0;
  }

  li + li {
    margin-top: 4px;
  }
}

.form-tip {
  margin: 5px 0 0 24px;
  color: #8c8c8c;
  line-height: 19px;
}

.matched-user-info {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
  margin-top: 6px;
}

.dual-user-id {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  min-width: 0;

  > small {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #9aa1ac;
    font-size: 11px;
  }
}

.list-pagination {
  margin-top: 16px;
}

.missing-order-result {
  min-height: 220px;
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;

  > div {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  span {
    color: #808695;
    font-size: 12px;
  }
}

.section-heading.compact {
  margin-bottom: 10px;
}

.platform-summary {
  margin-bottom: 18px;
}

.candidate-section {
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid #f0f0f0;
}

.missing-supplement-form {
  /deep/ .ivu-alert {
    margin-bottom: 16px;
  }
}

.equity-estimate-card {
  margin-bottom: 16px;
  overflow: hidden;
  border: 1px solid #e8eaec;
  border-radius: 4px;

  > .ivu-alert {
    margin: 12px;
  }
}

.estimate-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 72px;
  margin-bottom: 16px;
  color: #808695;
}

.mono {
  font-family: Consolas, Monaco, monospace;
  font-size: 13px;
}

.coin-change {
  overflow: hidden;
  color: #fa541c;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;

  span {
    display: inline;
  }

  span + span {
    margin-left: 4px;
  }
}

.amount-cell {
  display: flex;
  flex-direction: column;
  justify-content: center;
  line-height: 18px;

  small {
    color: #8c8c8c;
    font-size: 12px;
  }
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.empty-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 170px;
  color: #595959;

  p {
    margin-top: 10px;
  }
}

.modal-alert {
  margin-bottom: 16px;
}

.subscription-status-result {
  position: relative;
  min-height: 180px;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #17233d;
  font-size: 14px;
  font-weight: 600;
}

.mail-confirm-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 11px 12px;
  border: 1px solid #b7eb8f;
  border-radius: 8px;
  background: #f6ffed;
}

.description-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  overflow: hidden;
  border-top: 1px solid #e8eaec;
  border-left: 1px solid #e8eaec;
}

.order-description {
  margin-bottom: 16px;
}

.description-item {
  display: grid;
  grid-template-columns: 112px 1fr;
  min-height: 40px;
  border-right: 1px solid #e8eaec;
  border-bottom: 1px solid #e8eaec;

  > span:first-child {
    display: flex;
    align-items: center;
    padding: 8px 10px;
    background: #fafafa;
  }

  > strong,
  > .ivu-tag,
  > .copyable-text {
    align-self: center;
    min-width: 0;
    margin-left: 10px;
    font-weight: 400;
  }
}

.description-item-wrap {
  > strong {
    padding: 8px 10px 8px 0;
    overflow-wrap: anywhere;
    white-space: normal;
  }

  /deep/ .copyable-value {
    overflow: visible;
    text-overflow: clip;
    word-break: break-all;
    white-space: normal;
  }
}

.user-info-card {
  overflow: hidden;
  margin-bottom: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
}

.user-info-card-header {
  height: 42px;
  padding: 10px 16px;
  border-bottom: 1px solid #f0f0f0;
  font-weight: 600;
}

.user-info-grid {
  padding: 0 16px;
}

.user-info-row {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  align-items: stretch;
  min-height: 54px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: 0;
  }
}

.user-info-category {
  display: flex;
  align-items: center;
  color: #17233d;
  font-weight: 600;
}

.user-info-values {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: center;
  gap: 16px;
  padding: 10px 0;
}

.user-info-values-three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.user-info-values-single {
  grid-template-columns: minmax(0, 1fr);
}

.user-info-metric {
  display: grid;
  grid-template-columns: 82px minmax(0, 1fr);
  align-items: center;
  min-width: 0;

  > span {
    color: #808695;
  }

  > strong {
    min-width: 0;
    color: #17233d;
    font-weight: 500;
    overflow-wrap: anywhere;
  }
}

.currency-label {
  margin-left: 8px;
}

.refund-confirm {
  margin-bottom: 20px;
  text-align: center;

  h3 {
    margin: 10px 0 5px;
    color: #ff4d4f;
    font-size: 18px;
  }

  p {
    color: #8c8c8c;
  }
}

.description-list {
  overflow: hidden;
  margin-bottom: 16px;
  border-top: 1px solid #e8eaec;
  border-left: 1px solid #e8eaec;

  > div {
    display: grid;
    grid-template-columns: 140px 1fr;
    min-height: 40px;
    border-right: 1px solid #e8eaec;
    border-bottom: 1px solid #e8eaec;
  }

  > div > span:first-child {
    display: flex;
    align-items: center;
    padding: 8px 10px;
    background: #fafafa;
  }

  > div > .copyable-text {
    align-self: center;
    min-width: 0;
    margin: 8px 12px;
  }

  strong {
    align-self: center;
    padding: 8px 12px;
    font-weight: 400;
  }

  .refund-amount {
    color: #ff4d4f;
    font-size: 16px;
    font-weight: 600;
  }
}

.cancel-description {
  > div {
    grid-template-columns: 120px 1fr;
  }
}

.cancel-notice {
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #ffd591;
  border-radius: 8px;
  background: #fff7e6;
  color: #d46b08;

  strong {
    display: block;
    margin-bottom: 8px;
  }

  p {
    margin: 4px 0;
  }
}

.cancel-checkbox {
  margin-top: 2px;
}

@media (max-width: 1500px) {
  .query-card-body {
    grid-template-columns: minmax(230px, 0.7fr) minmax(460px, 1.35fr) auto auto;
  }

  .goods-filter-query {
    grid-column: 1 / 3;
  }
}

@media (max-width: 1000px) {
  .query-card-body {
    grid-template-columns: 1fr;
  }

  .platform-order-query {
    grid-template-columns: 1fr;
  }

  .goods-filter-query {
    grid-column: auto;
    grid-template-columns: 1fr;
  }
}
</style>
